import React, { useState } from 'react';

export default function SegmentEditorPanel({
  segments,
  coordinates,
  selectedSegIdx,
  editAction,
  isDirty,
  saving,
  onSelectSegment,
  onDeleteSegment,
  onRenameSegment,
  onMoveStart,
  onMoveEnd,
  onCreateNew,
  onCancelAction,
  onSave,
  onDiscard,
}) {
  const [editingLabel, setEditingLabel] = useState(null); // { segIdx, value }

  const startRename = (segIdx, currentLabel) => {
    setEditingLabel({ segIdx, value: currentLabel });
  };

  const commitRename = () => {
    if (editingLabel) {
      onRenameSegment(editingLabel.segIdx, editingLabel.value);
      setEditingLabel(null);
    }
  };

  const actionText = {
    pickStart: 'Click a track point to set new START',
    pickEnd: 'Click a track point to set new STOP',
    createStart: 'Click a track point for segment START',
    createEnd: 'Click a track point for segment STOP',
  };

  return (
    <div style={styles.panel} data-doc-id="segment-editor-panel">
      {/* Header */}
      <div style={styles.header}>
        <span style={styles.headerTitle}>SEGMENTS</span>
        <span style={styles.headerCount}>{segments.length}</span>
        <div style={{ flex: 1 }} />
        <button
          style={styles.newBtn}
          onClick={onCreateNew}
          disabled={!!editAction}
          data-doc-id="segment-new"
        >
          + New
        </button>
      </div>

      {/* Action status bar */}
      {editAction && (
        <div style={styles.actionBar}>
          <span style={styles.actionText}>{actionText[editAction] || 'Editing...'}</span>
          <button style={styles.cancelBtn} onClick={onCancelAction} data-doc-id="segment-cancel-action">Cancel</button>
        </div>
      )}

      {/* Segment list */}
      <div style={styles.list}>
        {segments.length === 0 ? (
          <div style={styles.empty}>No segments defined. Click "+ New" to create one.</div>
        ) : (
          segments.map((seg, i) => {
            const isSelected = selectedSegIdx === i;
            const isEditing = editingLabel?.segIdx === i;
            return (
              <div
                key={`${seg.startIdx}-${seg.endIdx}`}
                data-doc-id="segment-card"
                style={{
                  ...styles.card,
                  ...(isSelected ? styles.cardSelected : {}),
                }}
                onClick={() => onSelectSegment(i)}
              >
                {/* Label */}
                <div style={styles.cardHeader}>
                  {isEditing ? (
                    <input
                      style={styles.labelInput}
                      value={editingLabel.value}
                      onChange={(e) => setEditingLabel({ ...editingLabel, value: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename();
                        if (e.key === 'Escape') setEditingLabel(null);
                      }}
                      onBlur={commitRename}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span
                      style={styles.label}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        startRename(i, seg.label);
                      }}
                      title="Double-click to rename"
                    >
                      {seg.label || '(unnamed)'}
                    </span>
                  )}
                  <span style={styles.pointRange}>pts {seg.startIdx} &rarr; {seg.endIdx}</span>
                </div>

                {/* Actions */}
                <div style={styles.cardActions}>
                  <button
                    style={styles.actionBtn}
                    onClick={(e) => { e.stopPropagation(); startRename(i, seg.label); }}
                    disabled={!!editAction}
                    title="Rename segment"
                    data-doc-id="segment-rename"
                  >
                    Rename
                  </button>
                  <button
                    style={styles.actionBtn}
                    onClick={(e) => { e.stopPropagation(); onMoveStart(i); }}
                    disabled={!!editAction}
                    title="Click a new start point on the track"
                    data-doc-id="segment-move-start"
                  >
                    Move Start
                  </button>
                  <button
                    style={styles.actionBtn}
                    onClick={(e) => { e.stopPropagation(); onMoveEnd(i); }}
                    disabled={!!editAction}
                    title="Click a new end point on the track"
                    data-doc-id="segment-move-end"
                  >
                    Move End
                  </button>
                  <button
                    style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Delete segment "${seg.label || 'unnamed'}"?`)) {
                        onDeleteSegment(i);
                      }
                    }}
                    disabled={!!editAction}
                    title="Delete this segment"
                    data-doc-id="segment-delete"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Save bar */}
      {isDirty && (
        <div style={styles.saveBar} data-doc-id="segment-save-bar">
          <span style={styles.saveText}>Unsaved changes</span>
          <div style={styles.saveButtons}>
            <button style={styles.discardBtn} onClick={onDiscard} disabled={saving} data-doc-id="segment-discard">
              Discard
            </button>
            <button style={styles.saveBtn} onClick={onSave} disabled={saving} data-doc-id="segment-save">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  panel: {
    width: 320,
    minWidth: 320,
    background: 'var(--color-surface)',
    borderLeft: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    borderBottom: '1px solid var(--color-border)',
  },
  headerTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
  },
  headerCount: {
    fontFamily: 'var(--font-data)',
    fontSize: 11,
    color: 'var(--color-text-dim)',
    background: 'var(--color-bg)',
    borderRadius: 8,
    padding: '2px 8px',
  },
  newBtn: {
    padding: '5px 12px',
    background: 'var(--color-primary)',
    border: 'none',
    borderRadius: 4,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: 0.5,
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    background: 'rgba(255, 214, 0, 0.15)',
    borderBottom: '1px solid rgba(255, 214, 0, 0.3)',
  },
  actionText: {
    flex: 1,
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: '#FFD600',
    fontWeight: 600,
  },
  cancelBtn: {
    padding: '3px 10px',
    background: 'transparent',
    border: '1px solid rgba(255, 214, 0, 0.4)',
    borderRadius: 4,
    color: '#FFD600',
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    cursor: 'pointer',
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 12px',
  },
  empty: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-text-dim)',
    textAlign: 'center',
    padding: '32px 16px',
  },
  card: {
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    padding: '10px 12px',
    marginBottom: 8,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  cardSelected: {
    borderColor: 'var(--color-primary)',
    boxShadow: '0 0 0 1px var(--color-primary)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  label: {
    fontFamily: 'var(--font-heading)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text)',
    cursor: 'text',
  },
  labelInput: {
    fontFamily: 'var(--font-heading)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text)',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-primary)',
    borderRadius: 3,
    padding: '2px 6px',
    outline: 'none',
    width: '55%',
  },
  pointRange: {
    fontFamily: 'var(--font-data)',
    fontSize: 10,
    color: 'var(--color-text-dim)',
  },
  cardActions: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
  },
  actionBtn: {
    padding: '3px 8px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 3,
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-body)',
    fontSize: 10,
    cursor: 'pointer',
  },
  deleteBtn: {
    color: '#E74C3C',
    borderColor: 'rgba(231, 76, 60, 0.3)',
  },
  saveBar: {
    padding: '12px 16px',
    borderTop: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  saveText: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: '#FFD600',
    fontWeight: 600,
  },
  saveButtons: {
    display: 'flex',
    gap: 8,
  },
  discardBtn: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid rgba(231, 76, 60, 0.4)',
    borderRadius: 4,
    color: '#E74C3C',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveBtn: {
    padding: '6px 14px',
    background: 'var(--color-primary)',
    border: 'none',
    borderRadius: 4,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
};
